// pipe 4C: the pure, dependency-free routing SSOT for an ERP global-search result. Kept in its own
// file (no @toeverything/infra import) so it is unit-testable in isolation and importable by both the
// quicksearch session (impls/erp.ts) and the navigation branch (services/cmdk.ts).

/** What a chosen ERP result carries to the navigation branch in services/cmdk.ts. */
export interface ErpPayload {
  type: string;
  id: string;
  /** The server-computed station (4B.6) for staged/operational types; null for cross-cutting types. */
  stage: string | null;
  /** The query to pre-seed the destination screen's filter with, so the picked row is visible on
   *  arrival (the per-page filter then narrows the list to it). */
  seed: string;
  /** Whether the row is IMPORTED history rather than live work (`isLegacyRow`). Required, not
   *  optional, so a new call site must decide: defaulting it would silently route imported rows
   *  into a worklist that cannot contain them. */
  legacy: boolean;
}

/** PURE: whether a row came from a legacy import rather than being created natively in PIPE.
 *  Mirrors the server's own worklist admission rule — `worklist_for_stage` excludes rows with a
 *  `legacy_source` tag ("legacy rows are history, never live work"), so the client's routing must
 *  agree with it or it sends operators to guaranteed-empty screens. Takes a plain record so this
 *  file stays dependency-free and unit-testable in isolation. Fail-closed on shape: only a
 *  non-empty string counts as a provenance claim. */
export function isLegacyRow(
  data: Record<string, unknown> | null | undefined
): boolean {
  const src = data?.legacy_source;
  return typeof src === 'string' && src !== '';
}

/** IP2.6: the entity types that HAVE an Item Passport page (`/records/:id`) — the ONE SSOT for
 *  "does this type have a passport", consumed by the route arm below, the scan card's "Full
 *  history" action, the Records grid's title link, the job-detail rollups and the report id cell.
 *  Never a second predicate at a call site.
 *
 *  These are the four physical item types the passport's spine + contributors are built around
 *  (`part` 117,551 · `commodity_lot` 130,060 · `drive` 38,832 · `asset` 5,349 — plan §1.1). An
 *  `inbound_order` is deliberately absent: it has its own detail page, which arm 2 below serves. */
export const PASSPORT_TYPES = [
  'asset',
  'drive',
  'part',
  'commodity_lot',
] as const;

const PASSPORT_TYPE_SET: ReadonlySet<string> = new Set(PASSPORT_TYPES);

/** PURE: whether `type` has an Item Passport page. */
export function hasPassport(type: string): boolean {
  return PASSPORT_TYPE_SET.has(type);
}

/** IP2.6: the passport route for an item id. One builder so no call site hand-writes the path. */
export function passportRoute(id: string): string {
  return `/records/${encodeURIComponent(id)}`;
}

/**
 * Where a chosen ERP result routes. THE type → route SSOT, an ORDERED if-chain in which the arm
 * order is the substance:
 *
 * ```
 * 1. account              → /customers?id=&q=
 * 2. inbound_order        → /inbound/{id}
 * 3. hasPassport(type)    → /records/{id}          ← IP2.6
 * 4. p.stage && !p.legacy → /ops/worklist?stage=…  ← non-item staged types
 * 5. (fallthrough)        → /records?type=&id=&q=  ← the pinned-row landing
 * ```
 *
 * IP2.6 — ARM ORDER, stated so it is not "just an added arm". The passport arm sits BEFORE the
 * worklist arm, so it wins for every passport-bearing item type INCLUDING a native staged one.
 * Rationale: the passport is a SUPERSET of the worklist landing — it carries the same server-gated
 * `allowed_transitions` affordance, so the operator can still act on the item — whereas the
 * worklist is not a superset of the passport. Placing it AFTER the worklist arm would split item
 * destinations by a property (native vs imported) the operator cannot see, which is worse for the
 * scan case this was reported from.
 *
 * This is the ONE deliberate behaviour change of IP2: a scanned/searched NATIVE staged item now
 * lands on the item's history page instead of the station work queue. It is a judgement call, and
 * a one-line revert (drop this arm, so everything falls through to `erpListRoute`) if the floor
 * prefers the queue.
 */
export function erpResultRoute(p: ErpPayload): string {
  if (hasPassport(p.type)) return passportRoute(p.id);
  return erpListRoute(p);
}

/**
 * IP2: where a row RELATED to the item being viewed opens — a spine node, a timeline event's
 * evidence. PURE, so the passport container holds no routing knowledge of its own.
 *
 * `hint` is the SERVER's own `route_hint` (`/inbound`, `/qms/ncr`, `/records` — a public constant on
 * the wire) and wins when present: it is a statement about that row by the code that produced it.
 * With none, the destination is resolved BY TYPE through `erpResultRoute` — the ONE type→route
 * chain. The container previously assumed "no hint ⇒ it is an item, open its passport", which was a
 * second, private mapping: the spine emits every node openable and hintless, so an `inbound_order`
 * ancestor opened a passport page for a type deliberately EXCLUDED from `PASSPORT_TYPES`.
 *
 * `stage: null` / `legacy: true` are not placeholders — they are the only honest values available:
 * a spine row carries a LOCALIZED stage label rather than the wire token, and no provenance at all.
 * They feed only the live-worklist arm, and both fail closed away from it, because a queue that
 * structurally cannot contain an imported row is the dead end `isLegacyRow` exists to avoid.
 */
export function relatedRowRoute(
  id: string,
  type: string,
  hint: string | null
): string {
  if (hint) return `${hint}/${encodeURIComponent(id)}`;
  return erpResultRoute({ type, id, stage: null, legacy: true, seed: '' });
}

/** Arms 1/2/4/5 — where a row's LIST (or, for the two types that own one, DETAIL) screen is,
 *  pre-seeded with the search term. This is what a "Show in list" affordance wants: it must keep
 *  reaching a list even for a type that also has a passport, or the two card actions collapse into
 *  the same destination. `erpResultRoute` is this chain with the passport arm in front of it —
 *  one chain, not two mappings. */
export function erpListRoute(p: ErpPayload): string {
  const q = `q=${encodeURIComponent(p.seed)}`;
  // Every LIST destination also carries the picked row's id: the destination fetches THAT row by
  // id (the same scoped + redacted single-row read the result itself came through) and pins it, so
  // the landing always contains what was picked — whatever the page window, the stage queue's own
  // ordering, or a station narrowing would otherwise have returned. (Detail-page destinations
  // resolve the row by id already.)
  const id = `id=${encodeURIComponent(p.id)}`;
  if (p.type === 'account') return `/customers?${id}&${q}`;
  // An order routes to ITS detail page — the worklist is live work only (historical/completed
  // orders never appear there, so seeding it was a dead-end for search-to-history).
  if (p.type === 'inbound_order') return `/inbound/${p.id}`;
  // Same dead-end, one step generalized: the worklist is LIVE work only, and the server excludes
  // every `legacy_source`-tagged row from it. An imported part/lot DOES carry a stage (part →
  // sort), so seeding its worklist would land a scanning operator on a screen that structurally
  // cannot show the row in their hand.
  if (p.stage && !p.legacy)
    return `/ops/worklist?stage=${encodeURIComponent(p.stage)}&${id}&${q}`;
  // Records is the destination — but "Records lists any registered type" is NOT the same as
  // "Records can show THIS row" (review finding): the screen fetches the NEWEST page of a type and
  // applies `?q=` as a CLIENT-side filter, so a deep-link carrying only the term lands on "no rows
  // match" for every row outside that page — i.e. essentially the whole import (117k parts / 130k
  // lots; the scanned label from the bug report ranks ~918th by the server's `created_at DESC`
  // order). A station-narrowed list (FullIsolation) returns nothing at all for an operator who
  // does not own the row's station, however new the row is. So the route carries the picked row's
  // `id`: the destination loads THAT row by id — the same scoped + redacted single-row read the
  // scan card itself came through — and pins it, so the landing always contains the row the
  // operator picked, whatever the page window or the station narrowing would have returned.
  return `/records?type=${encodeURIComponent(p.type)}&${id}&${q}`;
}
